/**
 * UGCPromo.jsx
 * Created by Destin Frasier on 12/16/20.
 */

import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useOffsetSize } from '../../../hooks';
import * as Icons from '../icons/Icons';
import ImageWithFallback from '../ImageWithFallback';
import FauxImage from '../FauxImage';

export default function UGCPromo({ ariaLabel, buttonCopy, href, showArrow, promoHeadingCopy, promoCopy, className }) {
    const targetRef = useRef();
    const { width } = useOffsetSize(targetRef);
    const { height } = useOffsetSize(targetRef);

    const arrow = (
        <span className="rec-ugc-promo-icon-wrap">
            <Icons.IconArrowForward />
        </span>
    );

    const promoButtonCopy = () => {
        return buttonCopy.length >= 1 ? (
            <span className="rec-ugc-promo-button-text">
                {buttonCopy}
                {arrow}
            </span>
        ) : (
            <span className="rec-ugc-promo-button-text">{showArrow && arrow}</span>
        );
    };

    const adjustLayout =
        width > 500 ? 'rec-ugc-promo-normal-img' : 'rec-ugc-promo-stacked';

    return (
        <a
            ref={targetRef}
            className={`rec-ugc-promo-wrap ${className}`}
            href={href}
            aria-label={ariaLabel}
            target="_blank"
            rel="noopener noreferrer"
        >
            <div className={cx('rec-ugc-promo-inner-wrap')}>
                <div className={`rec-ugc-promo-text-block ${adjustLayout}`}>
                    {width > 500 ? (
                        <ImageWithFallback
                            imagePath={`${process.env.CDN}/img/global/ugc/share_your_story_small`}
                            alt="Share Your Story on Recreation.gov"
                            fallbackExtension="png"
                            imgHeight="70px"
                            imgWidth="150px"
                        />
                    ) : (
                        <ImageWithFallback
                            imagePath={`${process.env.CDN}/img/global/ugc/share_your_story_horz`}
                            alt="Share Your Story on Recreation.gov"
                            imgHeight="45px"
                            imgWidth="265px"
                        />
                    )}
                    <p>
                        <strong>
                            {promoHeadingCopy}
                        </strong>&nbsp;
                        {promoCopy}&nbsp;
                        {promoButtonCopy()}
                    </p>
                </div>

                <FauxImage
                    src={`${process.env.CDN}/img/global/ugc/UGC-promo-bg.jpg`}
                    altText="Share Your Story Contest"
                    style={{ minHeight: `${height}px` }}
                />
            </div>
        </a>
    );
}

UGCPromo.propTypes = {
    /** Aria-Label for the promo */
    ariaLabel: PropTypes.string,

    /** Bold copy that comes before the main copy */
    promoHeadingCopy: PropTypes.string,

    /** Copy for the promo */
    promoCopy: PropTypes.string,

    /** Bold copy displayed directly before the arrow */
    buttonCopy: PropTypes.string,

    /** Url for the promo */
    href: PropTypes.string,

    // Show or hide the arrow directly after the promo copy */
    showArrow: PropTypes.bool,

    /** Added to existing classes */
    className: PropTypes.string,
};

UGCPromo.defaultProps = {
    ariaLabel: 'Enter the Share Your Story Contest',
    className: undefined,
    promoHeadingCopy: '',
    promoCopy: '',
    buttonCopy: 'Learn More',
    href: '/shareyourstory',
    showArrow: true,
};
